/**
 * Icon Category Configuration *
 * Defines attack surface-based categories for organizing icons in the IconPicker.
 * Categories map to icon registry sources via CATEGORY_TO_SOURCES mapping.
 */

import {
  AppWindow,
  Cloud,
  Code,
  Globe,
  Grid,
  Monitor,
  Network,
  Users,
} from 'lucide-react';
import type { ComponentType } from 'react';

import {
  AD_ICONS,
  AWS_ICONS,
  AZURE_ICONS,
  CICD_ICONS,
  GCP_ICONS,
  INFRASTRUCTURE_ICONS,
  IOT_ICONS,
  LUCIDE_ICONS,
  SOCIAL_ICONS,
  WEB_ICONS,
} from '@/sections/attacks/attackPaths/components/icons/types';

/**
 * Union type of all valid icon IDs.
 * Provides compile-time type safety for icon references.
 */
export type ValidIconId =
  | `infrastructure:${(typeof INFRASTRUCTURE_ICONS)[number]}`
  | `lucide:${(typeof LUCIDE_ICONS)[number]}`
  | `aws:${(typeof AWS_ICONS)[number]}`
  | `azure:${(typeof AZURE_ICONS)[number]}`
  | `gcp:${(typeof GCP_ICONS)[number]}`
  | `ad:${(typeof AD_ICONS)[number]}`
  | `cicd:${(typeof CICD_ICONS)[number]}`
  | `web:${(typeof WEB_ICONS)[number]}`
  | `iot:${(typeof IOT_ICONS)[number]}`
  | `social:${(typeof SOCIAL_ICONS)[number]}`;

export interface IconCategory {
  /** Unique identifier for the category */
  id: string;
  /** Display label for the category tab */
  label: string;
  /** Icon component for the tab */
  icon: ComponentType<{ className?: string }>;
}

/** Category ID type for type safety */
export type CategoryId =
  | 'all'
  | 'cloud'
  | 'code'
  | 'device'
  | 'internet'
  | 'intranet'
  | 'users'
  | 'web';

/**
 * Available icon categories for filtering.
 * Order determines display order in the tab bar.
 */
export const ICON_CATEGORIES: IconCategory[] = [
  { id: 'all', label: 'All', icon: Grid },
  { id: 'cloud', label: 'Cloud', icon: Cloud },
  { id: 'code', label: 'Code', icon: Code },
  { id: 'device', label: 'Device', icon: Monitor },
  { id: 'internet', label: 'Internet', icon: Globe },
  { id: 'intranet', label: 'Intranet', icon: Network },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'web', label: 'Web', icon: AppWindow },
] as const;

/**
 * Default category to show when IconPicker opens.
 */
export const DEFAULT_CATEGORY: CategoryId = 'all';

/**
 * Maps attack surface categories to icon source categories.
 * Icons are included if their `category` property matches any entry.
 *
 * Note: 'aws', 'azure', 'gcp', 'ad' match directly as categories
 * Empty arrays = "Coming soon" tabs (use CATEGORY_ICON_IDS for specifics)
 */
export const CATEGORY_TO_SOURCES: Record<CategoryId, string[]> = {
  all: [], // Special case: shows all icons
  cloud: ['aws', 'azure', 'gcp'], // Cloud provider icons
  code: ['cicd'], // CI/CD and DevOps tools (GitHub Actions, GitLab, Jenkins, etc.)
  device: ['iot'], // IoT and embedded hardware icons
  internet: [], // Use CATEGORY_ICON_IDS for curated selection
  intranet: ['ad'], // Active Directory icons
  users: ['social'], // Social engineering / user-targeted attack vectors
  web: ['web'], // Web technologies (React, Node.js, databases, etc.)
};

/**
 * Specific icon IDs to include in each category.
 * Used for curated icon selection (combined with CATEGORY_TO_SOURCES via OR logic).
 *
 * Icon IDs use format: 'source:name' (e.g., 'lucide:WebApp', 'aws:Lambda')
 */
export const CATEGORY_ICON_IDS: Partial<Record<CategoryId, ValidIconId[]>> = {
  internet: [
    'lucide:WebApp',
    'lucide:Network',
    'lucide:Router',
    'lucide:Firewall',
    'infrastructure:Server',
    'infrastructure:Computer',
    'infrastructure:Database',
  ],
  intranet: [
    // AD icons included via CATEGORY_TO_SOURCES
    // Plus these infrastructure icons:
    'infrastructure:Server',
    'infrastructure:Computer',
    'infrastructure:Database',
    'infrastructure:Network',
  ],
};
